import { useMemo } from "react";

import { uniqBy } from "lodash";

import { useCustomGoalsContext } from "@components/data/custom-goals-provider";
import { TitleSlide } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import { useCustomMetrics } from "@features/custom-metrics/hooks/use-custom-metrics";
import { useFeatureFlags } from "@hooks/http";
import {
  makeBenchmarkRows,
  makeFirstTimeVsReturningRows,
  makeOverviewRows,
  makeTrafficRows,
} from "@north-beam/nb-client-side/metrics";

import { useOverviewContext } from "../../hooks";
import { type TileDescriptor } from "../../types";

import { TileEllipsisMenu } from "./ellipsis-menu";

interface Props {
  tile: TileDescriptor;
  onAboutToDelete(): void;
}

export const ContentError = ({ tile, onAboutToDelete }: Props) => {
  const { user } = useUser();
  const { hasFeatureFlag } = useFeatureFlags();
  const { filters } = useOverviewContext();
  const { customGoals } = useCustomGoalsContext();
  const { customMetricData: cashCustomMetricData } = useCustomMetrics("cash");
  const { customMetricData: accrualCustomMetricData } =
    useCustomMetrics("accrual");

  const cashMetrics = useMemo(() => {
    const showV1Forecasts = false;

    const metrics = [
      ...makeOverviewRows(
        filters.attributionModel,
        "infinity",
        showV1Forecasts,
        "cash",
        customGoals,
        "daily",
        user,
      ),
      ...makeTrafficRows(
        filters.attributionModel,
        "infinity",
        "cash",
        user,
        "daily",
      ),
      ...makeFirstTimeVsReturningRows(
        filters.attributionModel,
        "infinity",
        "cash",
        "daily",
        user.attributionMethodEnum,
      ),
      ...makeBenchmarkRows(filters.attributionModel, "infinity"),
      ...cashCustomMetricData,
    ].map((metric) => {
      return {
        ...metric,
        id: metric.metadata?.overviewPageMetricId,
      };
    });

    return uniqBy(metrics, "id");
  }, [filters.attributionModel, user, customGoals, cashCustomMetricData]);

  const accrualMetrics = useMemo(() => {
    const showV1Forecasts =
      filters.attributionModel === "northbeam_custom" &&
      hasFeatureFlag("showV1Forecasts");

    const metrics = [
      ...makeOverviewRows(
        filters.attributionModel,
        "1",
        showV1Forecasts,
        "accrual",
        customGoals,
        "daily",
        user,
      ),
      ...makeTrafficRows(
        filters.attributionModel,
        "1",
        "accrual",
        user,
        "daily",
      ),
      ...makeFirstTimeVsReturningRows(
        filters.attributionModel,
        "1",
        "accrual",
        "daily",
        user.attributionMethodEnum,
      ),
      ...makeBenchmarkRows(filters.attributionModel, "1"),
      ...accrualCustomMetricData,
    ].map((metric) => {
      return {
        ...metric,
        id: metric.metadata?.overviewPageMetricId,
      };
    });

    return uniqBy(metrics, "id");
  }, [
    filters.attributionModel,
    user,
    customGoals,
    accrualCustomMetricData,
    hasFeatureFlag,
  ]);

  const foundMetric = useMemo(() => {
    const accrualMetric = accrualMetrics.find((e) => e.id === tile.metricId);
    const cashMetric = cashMetrics.find((e) => e.id === tile.metricId);

    return filters.accountingMode === "accrual" ? cashMetric : accrualMetric;
  }, [cashMetrics, accrualMetrics, filters.accountingMode, tile]);

  return (
    <div className="flex flex-col h-full">
      <div className="self-end">
        <TileEllipsisMenu
          tile={tile}
          showDeleteOnly
          onDelete={onAboutToDelete}
        />
      </div>
      <TitleSlide className="text-xs text-center mb-6">
        {foundMetric ? (
          <>
            <span className="font-bold">{foundMetric.name}</span> metric is
            supported only in{" "}
            {filters.accountingMode === "accrual" ? "cash" : "accrual"}{" "}
            accounting mode.
            <br />
            Switch accounting mode to see this tile!
          </>
        ) : (
          <>
            Could not find this Traffic Source.
            <br />
            Please contact Northbeam support!
          </>
        )}
      </TitleSlide>
    </div>
  );
};
